import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { toast } from '../../../../design-system/components';
import { trackFailed } from '../../../../../../shared-web/track';
import { ApiError } from '../../../auth/api-client';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, type GroupAnswer, type GroupCheck, type GroupPostPage as PostData, type GroupReaction, type GroupReply } from '../../api';
import { PageBack } from '../../components/PageBack';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { plural } from '../../format';
import { goBack, goGroup, navigate } from '../../router';
import { useKeptAcrossReload } from '../../use-kept-across-reload';
import { fitHeight } from '../friends/friend-parts';
import {
  CLOSE, CheckView, CheckingVeil, DeleteSheet, GROUP_LIMITS, GroupFaceView, GroupIcon, GroupPollView, HeldNote, MIC, MORE, PostWho, ReactButton, ReactionSummary, ReportSheet, SEND, SPEECH,
  ago, pollHint, pollMeta, refusalOf, upFirst, useDictation, useGroupsLive,
  type AnswerWord, type Refusal,
} from './group-parts';
import { withMine } from './group-reactions';

/** Where words are being written: an answer to the post (null), or a reply under one top-level answer (its id). */
type Target = string | null;
/** Theirs to delete: the post (`answerId` null), or one answer, reply or chain line; `under` — answers or replies go with it. */
interface Removing { answerId: string | null; what: 'post' | AnswerWord; under: boolean }

/**
 * A post — `/groups/p/:postId` (2026-11-03 founder; redesign 2026-11-04, mock Post, after Facebook's comments). The bar:
 * back, the group's icon and name, ···. Who asked and when and how many have seen it, the question in bold and its
 * background (or what was shared), its emoji and how many answered, React | Answer. Then the answers, oldest first, each
 * a bubble with the name inside (Ember's under its own name and flame, in a warm bubble), its emoji on the bubble's
 * corner, and when · React · Reply · ··· under it; replies indented under their answer on a thread line (one level: a
 * reply to a reply goes under the same answer). At the bottom, the box to write in (with the mic): an answer, or a reply
 * once Reply was tapped ("Replying to Sage"); Ember looks at it before it goes up, as it does a post. `?answer=1` (Ember
 * asked them to answer it, or the feed's Answer) puts them in the box. Their own post, answer or reply they can delete,
 * for everyone, with what is under it; anyone else's they can report (hidden for them after). Ember's chain: its opening
 * line, the lines numbered in the order they came, and the box adds theirs — one short line each, until theirs is there
 * (deleting it lets them add again). Ember's poll: its options to vote on, and replies under it like any post.
 */
export function GroupPostPage({ postId, answering }: { postId: string; answering: boolean }): ReactElement {
  const path = `/groups/p/${encodeURIComponent(postId)}`;
  const [page, setPage] = useState<PostData | null>(null);
  const [failed, setFailed] = useState<'' | 'not_open' | 'gone' | 'load_failed'>('');
  // A call for the box (?answer=1, words waiting from before): it takes the focus once the page is there.
  const [composing, setComposing] = useState(answering);
  const [text, setText] = useState('');
  const [reply, setReply] = useState<{ to: string; name: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState<AnswerWord | null>(null);
  const [check, setCheck] = useState<{ out: GroupCheck; to: Target; words: string } | null>(null);
  const [refusal, setRefusal] = useState<{ to: Target; r: Refusal } | null>(null);
  const [report, setReport] = useState<{ answerId: string | null } | null>(null);
  const [removing, setRemoving] = useState<Removing | null>(null);
  // What they just put up, to land it once the page has it; it clears itself when it has landed.
  const [justIn, setJustIn] = useState('');
  const dictation = useDictation();
  const box = useRef<HTMLTextAreaElement | null>(null);
  const keptKey = `ember.group-answer.${postId}`;
  useKeptAcrossReload(keptKey, text.trim() ? text : '', (kept) => { setText(kept); setComposing(true); });

  const toAuth = (): void => navigate(`/auth?returnTo=${encodeURIComponent(`${path}${answering ? '?answer=1' : ''}`)}`, { replace: true });

  const reads = useRef(0);
  const load = (): void => {
    const seq = ++reads.current;
    emberApi.groupPost(postId)
      .then((r) => { if (seq === reads.current) { setPage(r); setFailed(''); } })
      .catch((err: unknown) => {
        if (seq !== reads.current) return;
        if (isAuthRequired(err)) { toAuth(); return; }
        const code = apiErrorCode(err);
        // Not in its group yet: the group's page, which offers Join when the group is open to them.
        const groupOf = err instanceof ApiError ? (err.body as { group_id?: string } | null)?.group_id : '';
        if (code === 'not_open' && groupOf) { navigate(`/groups/${encodeURIComponent(groupOf)}`, { replace: true }); return; }
        if (code === 'not_open') { setFailed('not_open'); return; }
        if (code === 'post_not_found') { setFailed('gone'); return; }
        trackFailed('group_post_page', apiFailureReason(err));
        setFailed('load_failed');
      });
  };
  useEffect(load, [postId]);
  useGroupsLive(load);

  // The box called for (by ?answer=1, Answer, Reply, words waiting from before, or back from Ember's suggestion): in
  // view, ready to type.
  const focusBox = (): void => {
    const el = box.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest' });
  };
  useEffect(() => {
    if (composing && page && !check) focusBox();
  }, [composing, Boolean(page), Boolean(check)]);
  useEffect(() => {
    if (reply && !check) focusBox();
  }, [reply?.to, Boolean(check)]);
  // Words that arrive by the mic, a draft or a suggestion grow the box as typing does.
  const barText = reply ? reply.text : text;
  useEffect(() => { fitHeight(box.current); }, [barText, Boolean(check)]);

  if (failed === 'not_open') return <PageFailure title="This post is in a group that isn’t open to you" detail="Groups open up based on what you’ve talked about with Ember." logState="not_open" />;
  if (failed === 'gone') return <PageFailure title="This post isn’t here anymore" logState="not_found" />;
  if (!page && failed) return <PageFailure title="Couldn’t open this post" logState="load_failed" retry />;
  if (!page) return <PageLoading />;

  const { group, post, emoji } = page;
  const groupPath = `/groups/${encodeURIComponent(group.id)}`;
  const mine = post.author.me;
  const chain = post.kind === 'chain';
  const answerWord: AnswerWord = chain ? 'line' : post.kind === 'question' && !mine ? 'answer' : 'reply';
  const added = chain && post.answers.some((a) => a.author.me);
  const refusedAt = (to: Target): Refusal | null => (refusal && refusal.to === to ? refusal.r : null);

  /** Words back in the box they came from (the suggestion to change, or the words that did not go up). */
  const putBack = (to: Target, words: string): void => {
    if (to) setReply((cur) => ({ to, name: cur?.to === to ? cur.name : '', text: words }));
    else { setReply(null); setText(words); setComposing(true); }
  };

  const refused = (err: unknown, to: Target): void => {
    if (isAuthRequired(err)) { toAuth(); return; }
    const code = apiErrorCode(err);
    if (code === 'not_open') { setFailed('not_open'); return; }
    if (code === 'post_not_found') { setFailed('gone'); return; }
    setCheck(null);
    setRefusal({ to, r: refusalOf(err, { text: to ? 'Your reply' : `Your ${answerWord}` }) });
  };

  const send = async (to: Target, words: string): Promise<void> => {
    const { answer_id: landed } = await emberApi.answerGroupPost(postId, words, to);
    setJustIn(landed);
    if (to) setReply(null);
    else {
      try { window.sessionStorage.removeItem(keptKey); } catch { /* nothing kept */ }
      setText(''); setComposing(false);
    }
    setCheck(null); setRefusal(null);
    load();
  };

  /** Ember's look, then up: as it is, or "Ember suggests …" first. */
  const next = async (to: Target, words: string): Promise<void> => {
    if (busy || !words) return;
    dictation.stop();
    setBusy(true); setChecking(to ? 'reply' : answerWord); setRefusal(null);
    try {
      const out = await emberApi.checkGroupWords(group.id, { kind: 'answer', text: words });
      if (out.changes.length && out.suggested) { setCheck({ out, to, words }); return; }
      await send(to, words);
    } catch (err) {
      trackFailed('group_check', apiFailureReason(err), { match_id: group.id, state: to ? 'reply' : 'answer' });
      refused(err, to);
    } finally {
      setBusy(false); setChecking(null);
    }
  };

  const take = async (): Promise<void> => {
    if (busy || !check) return;
    const { to } = check;
    const words = check.out.suggested?.text ?? check.words;
    setBusy(true);
    try {
      await send(to, words);
    } catch (err) {
      trackFailed(to ? 'group_reply' : 'group_answer', apiFailureReason(err), { match_id: group.id });
      putBack(to, words);
      refused(err, to);
    } finally {
      setBusy(false);
    }
  };

  // Emoji on the post (null) or an answer or reply (its id): drawn at once, the server's answer for the item then stands.
  const setReactions = (itemId: string | null, list: GroupReaction[]): void => {
    const on = <T extends GroupReply>(x: T): T => (x.id === itemId ? { ...x, reactions: list } : x);
    setPage((cur) => (!cur ? cur : {
      ...cur,
      post: itemId === null
        ? { ...cur.post, reactions: list }
        : { ...cur.post, answers: cur.post.answers.map((a) => ({ ...on(a), replies: a.replies.map(on) })) },
    }));
  };
  const react = async (itemId: string | null, current: GroupReaction[], chosen: string | null): Promise<void> => {
    setReactions(itemId, withMine(current, chosen));
    try {
      const out = await emberApi.reactInGroup(postId, chosen, itemId);
      setReactions(itemId, out.reactions);
    } catch (err) {
      trackFailed('group_react', apiFailureReason(err), { match_id: group.id, state: itemId ? 'answer' : 'post' });
      setReactions(itemId, current);
      toast.error('Couldn’t add that. Try again?');
    }
  };

  const sendReport = async (answerId: string | null): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await emberApi.reportGroupPost(postId, answerId);
      setReport(null);
      toast.success('Thanks for telling us. You won’t see it again.');
      if (answerId) {
        setPage((cur) => (cur ? {
          ...cur,
          post: { ...cur.post, answers: cur.post.answers.filter((a) => a.id !== answerId).map((a) => ({ ...a, replies: a.replies.filter((r) => r.id !== answerId) })) },
        } : cur));
      } else goBack(null, groupPath);
    } catch (err) {
      trackFailed('group_report', apiFailureReason(err), { match_id: group.id });
      toast.error('Couldn’t send that just now. Try again?');
    } finally {
      setBusy(false);
    }
  };

  // Theirs, deleted: the post — back to the group; an answer, reply or line — this page read again.
  const remove = async ({ answerId, what }: Removing): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      if (answerId) await emberApi.deleteGroupAnswer(postId, answerId);
      else await emberApi.deleteGroupPost(postId);
      setRemoving(null);
      toast.success('Deleted.');
      if (answerId) load();
      else goGroup(group.id);
    } catch (err) {
      trackFailed('group_delete', apiFailureReason(err), { match_id: group.id, state: what });
      const code = apiErrorCode(err);
      // Gone already: the page as it now stands.
      if (code === 'post_not_found') { setRemoving(null); setFailed('gone'); return; }
      if (code === 'answer_not_found') { setRemoving(null); load(); return; }
      toast.error('Couldn’t delete that just now. Try again?');
    } finally {
      setBusy(false);
    }
  };

  // A vote in the poll: the counts come back with it.
  const vote = async (choice: number): Promise<void> => {
    try {
      const out = await emberApi.voteInGroup(postId, choice);
      setPage((cur) => (cur ? { ...cur, post: { ...cur.post, poll: out.poll } } : cur));
    } catch (err) {
      trackFailed('group_vote', apiFailureReason(err), { match_id: group.id, state: 'post' });
      toast.error('Couldn’t save your vote. Try again?');
    }
  };

  if (check) {
    return (
      <CheckView
        kind="answer"
        word={check.to ? 'reply' : answerWord}
        check={check.out}
        busy={busy}
        notice=""
        onTake={() => { void take(); }}
        onEdit={() => { putBack(check.to, check.out.suggested?.text ?? check.words); setCheck(null); }}
      />
    );
  }

  /** Reply under the top-level answer `under`, to the person whose words were tapped. */
  const openReply = (under: string, name: string): void => {
    dictation.stop();
    setReply(reply?.to === under ? { ...reply, name } : { to: under, name, text: '' });
    if (refusal?.to && refusal.to !== under) setRefusal(null);
    focusBox();
  };
  /** The box back to answering the post (Answer, or the reply let go). */
  const openAnswer = (): void => {
    dictation.stop();
    setReply(null);
    if (refusal?.to) setRefusal(null);
    setComposing(true);
    focusBox();
  };

  /**
   * One answer, reply or chain line: a bubble with the name inside and its emoji on the corner; under it when · React ·
   * Reply (which answers under the top-level answer `under`; a line has none) · ··· (theirs: delete; anyone else's:
   * report). `thread` — the replies under an answer.
   */
  const said = (item: GroupReply | GroupAnswer, under: string, kind: AnswerWord, n = 0, thread: ReactNode = null): ReactElement => {
    const name = item.author.me ? 'You' : item.author.name;
    return (
      <div className={`gr-said${kind === 'reply' ? ' is-reply' : ''}${item.id === justIn ? ' is-in' : ''}`} key={item.id}
        onAnimationEnd={() => { if (item.id === justIn) setJustIn(''); }}>
        {kind === 'line' && <span className="gr-line-n" aria-hidden>{n}</span>}
        <GroupFaceView face={item.author} size={kind === 'reply' ? 26 : 34} />
        <div className="gr-said-col">
          <div className={`gr-bubble${item.author.ember ? ' is-ember' : ''}${item.reactions.length ? ' has-sum' : ''}`}>
            <span className="gr-bubble-name" dir="auto">{name}</span>
            <p className="gr-bubble-text" dir="auto">{item.text}</p>
            <ReactionSummary reactions={item.reactions} className="gr-bubble-sum" />
          </div>
          <div className={`gr-said-meta${item.reactions.length ? ' is-low' : ''}`}>
            <span>{upFirst(ago(item.at))}</span>
            <ReactButton reactions={item.reactions} emoji={emoji} groupId={group.id} what={kind} look="word" onReact={(e) => { void react(item.id, item.reactions, e); }} />
            {kind !== 'line' && (
              <button type="button" className="gr-word" data-log="group_reply_open" data-log-state={kind} data-log-match-id={group.id} onClick={() => openReply(under, item.author.me ? '' : item.author.name)}>
                Reply
              </button>
            )}
            {item.author.me ? (
              <button type="button" className="gr-word is-more" aria-label={`Delete your ${kind === 'answer' ? answerWord : kind}`} data-log="group_delete" data-log-state={`ask_${kind}`} data-log-match-id={group.id}
                onClick={() => setRemoving({ answerId: item.id, what: kind === 'answer' ? answerWord : kind, under: 'replies' in item && item.replies.length > 0 })}>
                {MORE}
              </button>
            ) : (
              <button type="button" className="gr-word is-more" aria-label={`Report this ${kind}`} data-log="group_report" data-log-state={`ask_${kind}`} data-log-match-id={group.id} onClick={() => setReport({ answerId: item.id })}>
                {MORE}
              </button>
            )}
          </div>
          {thread}
        </div>
      </div>
    );
  };

  // The box at the bottom: an answer (or a reply, or a chain's line) to the post, or a reply under an answer.
  const writing = chain ? !added : true;
  const barRefusal = refusedAt(reply ? reply.to : null);
  const fieldRefused = barRefusal && 'field' in barRefusal ? barRefusal.message : '';
  const lineLeft = GROUP_LIMITS.LINE - text.length;
  const setBar = (v: string): void => {
    if (reply) setReply({ ...reply, text: v });
    else setText(chain ? v.replace(/\s*\n\s*/g, ' ').slice(0, GROUP_LIMITS.LINE) : v);
    if (barRefusal && 'field' in barRefusal) setRefusal(null);
  };
  const placeholder = reply ? 'Write a reply…' : chain ? 'Add your line…' : answerWord === 'answer' ? 'Write an answer…' : 'Write a reply…';
  const listening = dictation.listening === 'group-bar';
  const n = post.answer_count;
  const count = chain ? (n > 0 ? `${plural(n, 'line')} so far` : 'No lines yet')
    : post.kind === 'question' ? (n > 0 ? plural(n, 'answer') : 'No answers yet')
      : (n > 0 ? plural(n, 'reply', 'replies') : 'No replies yet');
  const meta = post.kind === 'poll' && post.poll ? pollMeta(post.poll)
    : chain ? 'everyone adds one line'
      : post.seen > 0 ? `seen by ${post.seen}` : '';

  return (
    <main className="nf-page gr-page gr-postpage" data-log-seen="group_post_page" data-log-state={post.kind} data-log-match-id={group.id}>
      {checking ? <CheckingVeil what={checking} /> : null}
      <header className="gr-topbar">
        <PageBack to={groupPath} className="gr-topbar-back" />
        <GroupIcon groupId={group.id} art={group.art} size={28} />
        <span className="gr-topbar-name" dir="auto">{group.name}</span>
        {mine ? (
          <button type="button" className="nf-iconbtn" aria-label="Delete this post" data-log="group_delete" data-log-state="ask_post" data-log-match-id={group.id}
            onClick={() => setRemoving({ answerId: null, what: 'post', under: post.answer_count > 0 })}>
            {MORE}
          </button>
        ) : (
          <button type="button" className="nf-iconbtn" aria-label="Report this post" data-log="group_report" data-log-state="ask" data-log-match-id={group.id} onClick={() => setReport({ answerId: null })}>
            {MORE}
          </button>
        )}
      </header>

      <section className="gr-op">
        <PostWho post={post} size={44} meta={meta} />
        {post.kind === 'share'
          ? <h1 className="gr-op-text" dir="auto">{post.body}</h1>
          : <h1 className="gr-op-title" dir="auto">{post.question}</h1>}
        {post.kind !== 'share' && post.body && <p className="gr-op-text" dir="auto">{post.body}</p>}
        {post.kind === 'poll' && post.poll && (
          <>
            <GroupPollView poll={post.poll} onVote={vote} groupId={group.id} where="post" />
            <span className="gr-op-hint">{pollHint(post.poll)}</span>
          </>
        )}
        <div className="gr-counts">
          <ReactionSummary reactions={post.reactions} />
          <span className="gr-counts-none">{count}</span>
        </div>
      </section>
      <div className="gr-acts is-op">
        <ReactButton reactions={post.reactions} emoji={emoji} groupId={group.id} what="post" look="bar" onReact={(e) => { void react(null, post.reactions, e); }} />
        {writing && (
          <button type="button" className="gr-act" data-log="group_answer" data-log-state="open" data-log-match-id={group.id} onClick={openAnswer}>
            {SPEECH}{chain ? 'Add your line' : answerWord === 'answer' ? 'Answer' : 'Reply'}
          </button>
        )}
      </div>

      <div className="gr-answers-head">
        <h2>{chain ? 'Lines' : post.kind === 'question' ? 'Answers' : 'Replies'}</h2>
        {post.answers.length > 1 && <span>Oldest first</span>}
      </div>
      <div className="gr-answers">
        {post.answers.length === 0 && (
          <p className="gr-empty">{chain ? 'No lines yet. Yours could be the first.' : mine ? 'No answers yet. Everyone in the group can see it now, and you’ll hear when someone answers.' : post.kind === 'question' ? 'No answers yet. Yours could be the first.' : 'No replies yet.'}</p>
        )}
        {chain
          ? post.answers.map((a, i) => said(a, a.id, 'line', i + 1))
          : post.answers.map((a) => said(a, a.id, 'answer', 0, a.replies.length > 0 ? (
            <div className="gr-thread">{a.replies.map((r) => said(r, a.id, 'reply'))}</div>
          ) : null))}
      </div>

      {writing && (
        <div className="gr-bar">
          {reply && (
            <div className="gr-bar-to">
              <span>{reply.name ? <>Replying to <strong dir="auto">{reply.name}</strong></> : 'Replying in this thread'}</span>
              <button type="button" className="gr-bar-cancel" aria-label="Cancel reply" disabled={busy} data-log="group_reply" data-log-state="cancel" data-log-match-id={group.id}
                onClick={() => { dictation.stop(); setReply(null); setRefusal(null); }}>
                {CLOSE}
              </button>
            </div>
          )}
          {barRefusal && 'held' in barRefusal && <HeldNote resources={barRefusal.held} what="answer" />}
          {barRefusal && 'note' in barRefusal && <p className="gr-refused" role="alert">{barRefusal.note}</p>}
          {fieldRefused && <p id="group-bar-refused" className="gr-refused" role="alert">{fieldRefused}</p>}
          {listening && <span className="gr-field-listening" role="status">Listening… tap the mic when you’re done.</span>}
          {!listening && barText.trim() && (
            <span className="gr-bar-as">
              You’ll show up as <strong dir="auto">{group.me.name}</strong>. Ember takes a quick look first.
              {chain && lineLeft <= 40 && <span className="gr-count" aria-live="polite"> {lineLeft} left</span>}
            </span>
          )}
          <div className="gr-bar-row">
            <GroupFaceView face={group.me} size={34} />
            <textarea
              id="group-bar"
              ref={box}
              className={`gr-bar-input${listening ? ' is-listening' : ''}`}
              value={barText}
              rows={1}
              maxLength={chain && !reply ? GROUP_LIMITS.LINE : GROUP_LIMITS.ANSWER}
              placeholder={placeholder}
              aria-label={placeholder.replace('…', '')}
              dir="auto"
              aria-invalid={Boolean(fieldRefused)}
              aria-describedby={fieldRefused ? 'group-bar-refused' : undefined}
              onChange={(e) => { if (listening) dictation.stop(); setBar(e.target.value); }}
            />
            {dictation.supported && !busy && (
              <button
                type="button"
                className={`gr-bar-btn gr-mic-btn${listening ? ' is-on' : ''}`}
                aria-label={listening ? 'Stop listening' : 'Say it instead'}
                aria-pressed={listening}
                data-log="group_mic"
                data-log-state={listening ? 'stop' : 'start'}
                onClick={() => (listening ? dictation.stop() : dictation.start('group-bar', barText, setBar))}
              >
                {MIC}
              </button>
            )}
            {barText.trim() && (
              <button
                type="button"
                className="gr-bar-btn gr-send"
                aria-label={reply ? 'Send reply' : chain ? 'Add your line' : `Send ${answerWord}`}
                disabled={busy}
                data-log={reply ? 'group_reply' : 'group_check'}
                data-log-state={reply ? 'next' : chain ? 'line' : 'answer'}
                data-log-match-id={group.id}
                onClick={() => { void next(reply ? reply.to : null, barText.trim()); }}
              >
                {SEND}
              </button>
            )}
          </div>
        </div>
      )}

      {report && (
        <ReportSheet answer={Boolean(report.answerId)} busy={busy} groupId={group.id} onReport={() => { void sendReport(report.answerId); }} onClose={() => setReport(null)} />
      )}
      {removing && (
        <DeleteSheet what={removing.what} under={removing.under} postKind={post.kind} busy={busy} groupId={group.id}
          onDelete={() => { void remove(removing); }} onClose={() => setRemoving(null)} />
      )}
    </main>
  );
}
